import type { PlanningInterrupt } from '@/common/components/wavvest-planning/types';
import {
  extractConversationMessagesFromUpdate,
  extractNodeNamesFromUpdate,
  extractVisualizationHtmlFromUpdate,
} from '@/common/components/wavvest-planning/orchestrator-message-adapter';

// ---------------------------------------------------------------------------
// Raw event shapes emitted by weave-x over WebSocket
// ---------------------------------------------------------------------------

export type PlanningWsRawEvent =
  | { type: 'metadata'; payload: Record<string, unknown> }
  | { type: 'updates'; payload: Record<string, unknown> }
  | { type: 'custom'; payload: Record<string, unknown> }
  | { type: 'interrupt'; payload: Record<string, unknown> }
  | { type: 'stream'; payload: Record<string, unknown> }
  | { type: 'tool_call'; payload: Record<string, unknown> }
  | { type: 'tool_result'; payload: Record<string, unknown> }
  | { type: 'tool_error'; payload: Record<string, unknown> }
  | { type: 'error'; payload: Record<string, unknown> }
  | { type: 'complete'; payload: { thread_id: string; run_id?: string | null } };

// ---------------------------------------------------------------------------
// Normalized event shapes consumed by React components / Redux
// ---------------------------------------------------------------------------

export type PlanningWsParsedEvent =
  | { kind: 'run_metadata'; threadId?: string; runId?: string | null }
  | {
      kind: 'message';
      source: 'stream' | 'update';
      sender: 'human' | 'ai' | 'system' | 'tool';
      content: string;
    }
  | { kind: 'visualization'; html: string }
  | { kind: 'node_status'; nodeNames: string[] }
  | { kind: 'tool_status'; status: 'start' | 'end' | 'error'; tool?: string }
  | { kind: 'pdf_progress'; payload: { event: string; [key: string]: unknown }; done: boolean }
  | { kind: 'interrupt'; interrupt: PlanningInterrupt }
  | { kind: 'complete'; threadId?: string; runId?: string | null }
  | { kind: 'error'; message: string; raw?: unknown }
  | { kind: 'unknown'; raw: unknown };

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Recursively finds the first `run_id` or `runId` string value in any nested
 * object/array structure — matches the logic originally in conversation-panel.
 */
function findRunId(value: unknown): string | null {
  if (!value) return null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const result = findRunId(item);
      if (result) return result;
    }
    return null;
  }
  if (isRecord(value)) {
    const direct = value.run_id ?? value.runId;
    if (typeof direct === 'string' && direct.length > 0) return direct;
    for (const key of Object.keys(value)) {
      const result = findRunId(value[key]);
      if (result) return result;
    }
  }
  return null;
}

function isMissingDataPopup(value: Record<string, unknown>): boolean {
  return (
    typeof value.token === 'string' &&
    (Array.isArray(value.missing_profile) || Array.isArray(value.missing_domains))
  );
}

function isSourceConflictPopup(value: Record<string, unknown>): boolean {
  return (
    typeof value.conflict_id === 'string' &&
    typeof value.resolution_token === 'string' &&
    typeof value.conflict_signature === 'string' &&
    Array.isArray(value.candidates)
  );
}

function withEntryId(payload: Record<string, unknown>, entryId: unknown): Record<string, unknown> {
  return typeof entryId === 'string' && typeof payload.id !== 'string'
    ? { ...payload, id: entryId }
    : payload;
}

function parseInterruptCandidate(
  candidate: unknown,
  entryId?: unknown,
  allowTypedWrapper = true,
): PlanningInterrupt | null {
  if (!isRecord(candidate)) {
    return null;
  }

  if ('value' in candidate) {
    return parseInterruptCandidate(
      candidate.value,
      typeof candidate.id === 'string' ? candidate.id : entryId,
      allowTypedWrapper,
    );
  }

  if (allowTypedWrapper && typeof candidate.type === 'string' && 'payload' in candidate) {
    return {
      type: candidate.type,
      payload: isRecord(candidate.payload)
        ? withEntryId(candidate.payload, entryId ?? candidate.id)
        : withEntryId({}, entryId ?? candidate.id),
    };
  }

  if (isMissingDataPopup(candidate)) {
    return {
      type: 'missing-data',
      payload: withEntryId(candidate, entryId ?? candidate.id),
    };
  }

  if (candidate.kind === 'staged_files_gate' && typeof candidate.token === 'string') {
    return {
      type: 'staged-files',
      payload: withEntryId(candidate, entryId ?? candidate.id),
    };
  }

  if (isSourceConflictPopup(candidate)) {
    return {
      type: 'source-conflict',
      payload: withEntryId(candidate, entryId ?? candidate.id),
    };
  }

  return null;
}

function parseInterruptCandidates(candidate: unknown): PlanningInterrupt | null {
  if (Array.isArray(candidate)) {
    for (const entry of candidate) {
      const result = parseInterruptCandidate(entry);
      if (result) return result;
    }
    return null;
  }

  return parseInterruptCandidate(candidate);
}

function parseInterruptPayload(payload: Record<string, unknown>): PlanningInterrupt | null {
  const directPayload = parseInterruptCandidate(payload);
  if (directPayload) return directPayload;

  const data = payload.data;
  if (Array.isArray(data)) {
    return parseInterruptCandidates(data);
  }
  if (isRecord(data)) {
    return parseInterruptCandidate(data) ?? parseInterruptCandidates(data.__interrupt__);
  }

  return null;
}

function parseInterruptEvent(payload: Record<string, unknown>): PlanningWsParsedEvent[] {
  const interrupt = parseInterruptPayload(payload) ?? extractInterrupt(payload);
  return interrupt
    ? [{ kind: 'interrupt', interrupt }]
    : [{ kind: 'unknown', raw: { type: 'interrupt', payload } }];
}

/**
 * Reconstruct a pending interrupt from a persisted LangGraph thread-state
 * response (ENG-2748).
 *
 * When a run pauses on a HITL interrupt (e.g. a missing-data question) the pause
 * is surfaced in the `/state` response both at the top-level `interrupts` array
 * and under `tasks[].interrupts`. On a fresh reload/re-login the FE otherwise
 * only learns about interrupts from the live WS stream, so the pause is lost and
 * the user is stranded on the last message with no way to continue. This reads
 * the same `{ id, value }` candidate shape the live parser already handles, so
 * the interrupt card can be rebuilt on restore.
 */
export function parseInterruptFromThreadState(state: unknown): PlanningInterrupt | null {
  if (!isRecord(state)) {
    return null;
  }

  const fromInterrupts = parseInterruptCandidates(state.interrupts);
  if (fromInterrupts) {
    return fromInterrupts;
  }

  if (Array.isArray(state.tasks)) {
    for (const task of state.tasks) {
      if (isRecord(task)) {
        const fromTask = parseInterruptCandidates(task.interrupts);
        if (fromTask) {
          return fromTask;
        }
      }
    }
  }

  return null;
}

function parseNestedInterrupt(value: Record<string, unknown>): PlanningInterrupt | null {
  const self = parseInterruptCandidate(value, undefined, false);
  if (self) return self;

  const direct = parseInterruptCandidates(value.interrupt ?? value.__interrupt__);
  if (direct) return direct;

  return null;
}

/**
 * Detects whether a payload contains an interrupt-like shape.
 * Interrupts may arrive inside `custom` or `updates`, including nested v4 node
 * partial-state shapes such as `{ orchestrator: { __interrupt__: ... } }`.
 */
function extractInterrupt(payload: Record<string, unknown>): PlanningInterrupt | null {
  const visit = (value: unknown): PlanningInterrupt | null => {
    if (Array.isArray(value)) {
      for (const item of value) {
        const result = visit(item);
        if (result) return result;
      }
      return null;
    }
    if (!isRecord(value)) {
      return null;
    }

    const direct = parseNestedInterrupt(value);
    if (direct && direct.type) return direct;

    for (const key of Object.keys(value)) {
      if (key === 'interrupt' || key === '__interrupt__') {
        continue;
      }
      const result = visit(value[key]);
      if (result) return result;
    }
    return null;
  };

  return visit(payload);
}

// ---------------------------------------------------------------------------
// Per-event-type parse functions
// ---------------------------------------------------------------------------

function parseMetadata(payload: Record<string, unknown>): PlanningWsParsedEvent[] {
  const runId = findRunId(payload);
  const threadId = typeof payload.thread_id === 'string' ? payload.thread_id : undefined;
  return [{ kind: 'run_metadata', threadId, runId }];
}

function parseUpdates(payload: Record<string, unknown>): PlanningWsParsedEvent[] {
  const events: PlanningWsParsedEvent[] = [];
  // Keep human messages in the normalized stream. Normal chat turns are
  // de-duplicated by the conversation panel against their optimistic message,
  // while interrupt resumes rely on the backend's persisted HumanMessage as
  // the only source for showing the submitted follow-up answer in history.
  const messages = extractConversationMessagesFromUpdate(payload);

  const interrupt = extractInterrupt(payload);
  if (interrupt) {
    // A successful resume can immediately pause on the next follow-up. Preserve
    // the accepted answer before publishing the replacement interrupt so the
    // conversation history and pending card both advance atomically.
    for (const m of messages) {
      events.push({ kind: 'message', source: 'update', sender: m.sender, content: m.content });
    }
    events.push({ kind: 'interrupt', interrupt });
    return events;
  }

  // Streaming progress: the node names in an `updates` frame drive the
  // user-facing progress phase. Phase derivation lives in `streaming-progress.ts`
  // so this parser stays free of UI concerns.
  const nodeNames = extractNodeNamesFromUpdate(payload);
  if (nodeNames.length > 0) {
    events.push({ kind: 'node_status', nodeNames });
  }

  const html = extractVisualizationHtmlFromUpdate(payload);
  if (html) {
    events.push({ kind: 'visualization', html });
  }

  for (const m of messages) {
    events.push({ kind: 'message', source: 'update', sender: m.sender, content: m.content });
  }

  console.log('[planning-v2/ws] parsed updates frame', {
    nodeNames,
    hasVisualizationHtml: Boolean(html),
    messageCount: messages.length,
    waitingForExplicitComplete: true,
  });

  return events;
}

function parseCustom(payload: Record<string, unknown>): PlanningWsParsedEvent[] {
  const events: PlanningWsParsedEvent[] = [];

  const interrupt = extractInterrupt(payload);
  if (interrupt) {
    events.push({ kind: 'interrupt', interrupt });
    return events;
  }

  const eventType = typeof payload.event === 'string' ? payload.event : '';

  if (eventType === 'tool_error') {
    return parseError(payload, { type: 'custom', payload });
  }

  if (eventType === 'tool_end' && payload.tool === 'generate_visualization') {
    const html = typeof payload.toolResult === 'string' ? payload.toolResult : '';
    if (html) {
      events.push({ kind: 'visualization', html });
    }
    // Signal UI that loading is done even when html is absent
    events.push({ kind: 'tool_status', status: 'end', tool: 'generate_visualization' });
    return events;
  }

  // PDF / document processing progress — surface as a dedicated progress event
  // so the component updates progress state without rendering a tool bubble.
  if (
    eventType.startsWith('pdf_') ||
    eventType.startsWith('document_') ||
    eventType.startsWith('page_') ||
    eventType.startsWith('ocr_') ||
    eventType.startsWith('xml_')
  ) {
    events.push({
      kind: 'pdf_progress',
      payload: { ...payload, event: eventType },
      done: eventType === 'document_processing_complete',
    });
    return events;
  }

  // Unknown custom event — preserve raw for diagnostics.
  events.push({ kind: 'unknown', raw: { type: 'custom', payload } });
  return events;
}

function parseStream(payload: Record<string, unknown>): PlanningWsParsedEvent[] {
  const events: PlanningWsParsedEvent[] = [];

  if (!Array.isArray(payload.data)) return events;

  const first = payload.data[0] as Record<string, unknown> | undefined;
  if (!first) return events;

  const rawContent = first.content;
  const token =
    typeof rawContent === 'string'
      ? rawContent
      : Array.isArray(rawContent) &&
          isRecord(rawContent[0]) &&
          typeof rawContent[0].text === 'string'
        ? rawContent[0].text
        : null;

  if (token) {
    events.push({ kind: 'message', source: 'stream', sender: 'ai', content: token });
  }

  return events;
}

function parseToolCall(payload: Record<string, unknown>): PlanningWsParsedEvent[] {
  const tool = typeof payload.event === 'string' ? payload.event : undefined;
  return [{ kind: 'tool_status', status: 'start', tool }];
}

function parseToolResult(payload: Record<string, unknown>): PlanningWsParsedEvent[] {
  const events: PlanningWsParsedEvent[] = [];
  const html = typeof payload.html === 'string' ? payload.html : '';
  if (html) {
    events.push({ kind: 'visualization', html });
  }
  events.push({ kind: 'tool_status', status: 'end' });
  return events;
}

function parseError(
  payload: Record<string, unknown>,
  raw: PlanningWsRawEvent,
): PlanningWsParsedEvent[] {
  // Preserve raw for UserInterrupt detection by callers
  const message =
    typeof payload.message === 'string'
      ? payload.message
      : typeof (payload.data as Record<string, unknown> | undefined)?.error === 'string'
        ? ((payload.data as Record<string, unknown>).error as string)
        : 'An unexpected error occurred.';
  return [{ kind: 'error', message, raw }];
}

function parseComplete(payload: {
  thread_id: string;
  run_id?: string | null;
}): PlanningWsParsedEvent[] {
  return [{ kind: 'complete', threadId: payload.thread_id, runId: payload.run_id }];
}

// ---------------------------------------------------------------------------
// Main parser — the single entry point components should call
// ---------------------------------------------------------------------------

/**
 * Translates a single raw weave-x WebSocket frame into one or more normalized
 * `PlanningWsParsedEvent` values. Never throws; unknown event types produce an
 * `unknown` event so the UI degrades gracefully.
 */
export function parsePlanningWsEvent(raw: PlanningWsRawEvent): PlanningWsParsedEvent[] {
  try {
    switch (raw.type) {
      case 'metadata':
        return parseMetadata(raw.payload);
      case 'updates':
        return parseUpdates(raw.payload);
      case 'custom':
        return parseCustom(raw.payload);
      case 'interrupt':
        return parseInterruptEvent(raw.payload);
      case 'stream':
        return parseStream(raw.payload);
      case 'tool_call':
        return parseToolCall(raw.payload);
      case 'tool_result':
        return parseToolResult(raw.payload);
      case 'tool_error':
        return parseError(raw.payload, raw);
      case 'error':
        return parseError(raw.payload, raw);
      case 'complete':
        return parseComplete(raw.payload);
      default: {
        // TypeScript exhaustiveness — any future event type lands here safely
        const exhaustive: never = raw;
        return [{ kind: 'unknown', raw: exhaustive }];
      }
    }
  } catch (err) {
    // Defensive: parser must never crash the UI
    console.error('[planning-ws-event-parser] Unexpected error parsing event:', err, raw);
    return [{ kind: 'unknown', raw }];
  }
}
